import { useState } from "react";

import _chunk from "lodash/chunk";
import _groupBy from "lodash/groupBy";

export const handleProgressExport =
  (progressEachReq: number) => (prevState: number) => {
    const nextBufferValue = progressEachReq + prevState;
    if (nextBufferValue >= 100) return 100;
    return nextBufferValue;
  };

const useFetchingPartial = <P, M, R>({
  serviceFn,
  totalEachRequest,
  fieldKey,
  extendInfo,
}: {
  serviceFn: (params: P) => Promise<M>;
  totalEachRequest: number;
  fieldKey: string;
  extendInfo: R;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [bufferProgress, setBufferProgress] = useState(0);
  const [progress, setProgress] = useState(0);

  const handleFetchService = async <DataItem>(
    listData: DataItem[]
  ): Promise<{
    error: boolean;
    data?: M;
    message: { id?: number; message: string; rowId: number }[];
    errorList?: { id?: number; message: string; rowId: number }[];
  }> => {
    try {
      const params: P = {
        [fieldKey]: listData,
        ...extendInfo,
      } as P;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any = await serviceFn(params);

      return {
        error: false,
        data: result,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        message: (result?.data?.items || [])?.filter(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
          (item: any) => item?.message
        ),
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      return {
        error: true,
        data: undefined,
        message: [],
      };
    }
  };

  const handleService = async <DataItem>(listData: DataItem[]) => {
    let finalList: DataItem[] = [];
    const listParamChunk = _chunk(listData, totalEachRequest || 0);
    const totalPatches = listParamChunk.length;
    const progressValueEachRequest = Number(100 / totalPatches);

    setLoading(true);
    for (const element of listParamChunk) {
      setBufferProgress(handleProgressExport(progressValueEachRequest));
      const currentList = element || ([] as DataItem[]);
      // eslint-disable-next-line no-await-in-loop
      const result = await handleFetchService(currentList);
      const rowsErrorGroupBy = _groupBy(result?.message || [], "rowId");
      const rowsErrorIds = result?.message?.map((item) => item?.rowId);

      finalList = currentList
        .map((item: DataItem) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (rowsErrorIds?.includes(Number((item as any)?.rowId || ""))) {
            return {
              ...item,
              message:
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                rowsErrorGroupBy[((item as any)?.rowId as number) || ""]?.[0]
                  ?.message,
            };
          }
          return undefined;
        })
        .filter(Boolean) as DataItem[];
      setProgress(handleProgressExport(progressValueEachRequest));
    }
    setLoading(false);
    setBufferProgress(0);
    setProgress(0);

    return finalList;
  };

  return {
    loading,
    progress,
    bufferProgress,
    handleService,
  };
};

export default useFetchingPartial;
